import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const partnerBenefits = [
  {
    icon: 'ri-vip-crown-line',
    title: 'Verified Partner Badge',
    description: 'Get an exclusive verified badge on your profile that instantly builds trust with fans and brands.',
    gradient: 'from-purple-500 to-violet-500',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Higher Revenue Share',
    description: 'Partners keep up to 94-96% of earnings on our highest tier. The more you grow, the more you keep.',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    icon: 'ri-live-line',
    title: 'Priority Streaming',
    description: 'Dedicated server priority for live streams. No lag, no drops, just smooth broadcasts every time.',
    gradient: 'from-orange-500 to-amber-500',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Brand Deal Access-Coming Soon',
    description: 'Get matched with sponsors and brand deals tailored to your niche. We connect you to opportunities.',
    gradient: 'from-teal-500 to-emerald-500',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    description: 'Skip the queue. Partners get direct access to our creator success team via priority channels.',
    gradient: 'from-cyan-500 to-sky-500',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Advanced Analytics',
    description: 'Deep-dive dashboards with audience insights, revenue forecasting, and growth recommendations.',
    gradient: 'from-fuchsia-500 to-purple-500',
  },
];

const requirements = [
  {
    number: '2K+',
    label: 'Followers',
    description: 'Minimum active follower count on Vibe-Z or linked platforms.',
    icon: 'ri-user-follow-line',
  },
  {
    number: '5 Exclusive',
    label: 'Streams / Month',
    description: 'Consistent streaming schedule showing commitment to your community.',
    icon: 'ri-live-line',
  },
  {
    number: '50+',
    label: 'Avg. Viewers',
    description: 'Average concurrent viewers during your live broadcasts.',
    icon: 'ri-eye-line',
  },
  {
    number: '100%',
    label: 'Authentic Content',
    description: 'Original, human-created content. No bots, no fake engagement.',
    icon: 'ri-shield-check-line',
  },
];

const revenueTiers = [
  { tier: 'Creator', share: '92%', followers: 'Any', color: 'from-gray-400 to-gray-500' },
  { tier: 'Partner', share: '94%', followers: '2K+', color: 'from-purple-500 to-pink-500' },
  { tier: 'Elite', share: '96%', followers: '50K+', color: 'from-orange-500 to-amber-500' },
];

export default function PartnerPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const benefitsRef = useRef<HTMLDivElement>(null);
  const [benefitsVisible, setBenefitsVisible] = useState(false);

  const reqsRef = useRef<HTMLDivElement>(null);
  const [reqsVisible, setReqsVisible] = useState(false);

  const tiersRef = useRef<HTMLDivElement>(null);
  const [tiersVisible, setTiersVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBenefitsVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = reqsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setReqsVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = tiersRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTiersVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Startup Banner */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-2 px-3 sm:py-3 sm:px-6 text-center">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm">
          <i className="ri-award-line text-sm sm:text-lg"></i>
          <span className="font-semibold">Join the Vibe Verified Program — Applications Now Open!</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg shadow-md dark:shadow-gray-900/20' : 'top-[36px] sm:top-[44px] bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/28f622dd-215d-431d-8f03-9f76adf8a1b8.png"
                alt="Vibe-Z Logo"
                className="h-7 sm:h-8 md:h-9 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors whitespace-nowrap">Home</Link>
              <a href="#benefits" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors whitespace-nowrap">Benefits</a>
              <a href="#requirements" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors whitespace-nowrap">Requirements</a>
              <a href="#revenue" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors whitespace-nowrap">Revenue</a>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-gray-700 dark:text-gray-200 text-lg`}></i>
              </button>
              <a href="/auth/login" className="hidden sm:inline-block px-3 sm:px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap cursor-pointer">Login</a>
              <a href="/auth/creator-register" className="px-3 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-1 sm:gap-2 cursor-pointer">
                <span>Apply Now</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4 flex flex-col gap-3">
              <Link to="/" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <a href="#benefits" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
              <a href="#requirements" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Requirements</a>
              <a href="#revenue" className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Revenue</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-36 md:pt-44 pb-16 sm:pb-24 md:pb-32 px-4 sm:px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 25%, #7c3aed 60%, #db2777 90%, #f97316 100%)' }}>
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 pointer-events-none"></div>
        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.20) 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.15) 0%, transparent 70%)' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — text */}
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] mb-4 sm:mb-6">
                Become a <br />
                <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-pink-300 bg-clip-text text-transparent">Vibe Verified Partner</span>
              </h1>
              <p className="text-base sm:text-lg text-white/80 max-w-lg leading-relaxed mb-6 sm:mb-8">
                Unlock exclusive benefits, higher revenue share, and priority support. Join the elite tier of creators on Vibe-Z.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-8 sm:mb-10">
                <a href="/auth/creator-register" className="px-6 sm:px-8 py-3.5 sm:py-4 text-sm sm:text-base font-bold text-gray-900 bg-white rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap text-center cursor-pointer inline-flex items-center justify-center gap-2">
                  <i className="ri-rocket-line"></i>
                  Sign Up, It's Free
                </a>
                <a href="#benefits" className="px-6 sm:px-8 py-3.5 sm:py-4 text-sm sm:text-base font-bold text-white border-2 border-white/40 rounded-full hover:border-white/70 hover:bg-white/10 transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
                  See Benefits
                  <i className="ri-arrow-down-line"></i>
                </a>
              </div>
              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-6 sm:gap-10">
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-white">
                    94-96%
                  </div>
                  <div className="text-xs sm:text-sm text-white/60">Partner Revenue Share</div>
                </div>
                <div className="w-px h-10 bg-white/20 hidden sm:block"></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-white">
                    Be the First
                  </div>
                  <div className="text-xs sm:text-sm text-white/60">Active Partners</div>
                </div>
                <div className="w-px h-10 bg-white/20 hidden sm:block"></div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-white">
                    $2000
                  </div>
                  <div className="text-xs sm:text-sm text-white/60">Paid to Partners</div>
                </div>
              </div>
            </div>

            {/* Right — visual card stack */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                {/* Back card */}
                <div className="absolute top-8 -left-4 w-full rounded-3xl overflow-hidden border border-white/20" style={{ transform: 'rotate(-6deg)', background: 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(236,72,153,0.2))', backdropFilter: 'blur(12px)', aspectRatio: '4/5' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Creative%20content%20creator%20in%20modern%20studio%20with%20colorful%20neon%20lighting%2C%20confident%20pose%2C%20professional%20streaming%20setup%2C%20warm%20purple%20and%20pink%20ambient%20glow%2C%20high%20quality%20lifestyle%20photography%2C%20vibrant%20creative%20atmosphere&width=520&height=650&seq=partner-card-back&orientation=portrait"
                    alt="Creator studio"
                    className="w-full h-full object-cover object-top opacity-60"
                  />
                </div>
                {/* Middle card */}
                <div className="absolute top-4 left-4 w-full rounded-3xl overflow-hidden border border-white/30" style={{ transform: 'rotate(3deg)', background: 'linear-gradient(135deg, rgba(249,115,22,0.25), rgba(168,85,247,0.2))', backdropFilter: 'blur(12px)', aspectRatio: '4/5' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Young%20female%20streamer%20in%20stylish%20gaming%20setup%20with%20LED%20lights%2C%20cheerful%20expression%2C%20modern%20room%20with%20plants%2C%20warm%20orange%20and%20purple%20tones%2C%20professional%20portrait%20photography%2C%20authentic%20lifestyle%20scene&width=520&height=650&seq=partner-card-mid&orientation=portrait"
                    alt="Streamer"
                    className="w-full h-full object-cover object-top opacity-70"
                  />
                </div>
                {/* Front card */}
                <div className="relative rounded-3xl overflow-hidden border-2 border-white/40 shadow-2xl" style={{ aspectRatio: '4/5', background: 'linear-gradient(135deg, rgba(124,58,237,0.4), rgba(219,39,119,0.3))', backdropFilter: 'blur(16px)' }}>
                  <img
                    src="https://storage.readdy-site.link/project_files/f4f0a068-4d6e-4d8b-b9c5-f019c11b8e5c/ffd13e5f-4475-4938-9900-a55afdb14839_compressed_fro2.webp"
                    alt="Partner creator"
                    className="w-full h-full object-cover object-top"
                  />
                  {/* Overlay badge */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl p-4 border border-white/40">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <i className="ri-verified-badge-fill text-white text-lg"></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 dark:text-white text-sm">Lovethe Fro</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Vibe Verified</div>
                        </div>
                        <span className="ml-auto px-2 py-1 text-[10px] font-black text-purple-700 bg-purple-100 dark:bg-purple-900/40 rounded-full uppercase tracking-wide">Partner</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
                        <span className="flex items-center gap-1"><i className="ri-money-dollar-circle-line text-purple-500"></i> $5K/mo</span>
                        <span className="flex items-center gap-1"><i className="ri-user-follow-line text-purple-500"></i> 21.3K</span>
                        <span className="flex items-center gap-1"><i className="ri-live-line text-purple-500"></i> 12 streams</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" ref={benefitsRef} className="w-full bg-white dark:bg-gray-950 py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-14 sm:mb-18"
            style={{
              opacity: benefitsVisible ? 1 : 0,
              transform: benefitsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 mb-5">
              <i className="ri-star-smile-line text-purple-600 dark:text-purple-400"></i>
              <span className="text-xs font-bold uppercase tracking-widest text-purple-700 dark:text-purple-300">Why Partner?</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white leading-tight mb-4">
              Perks That <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Actually Matter</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-relaxed">
              We built the Vibe Verified program around what creators actually need to grow, earn more, and stress less.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {partnerBenefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-7 border border-gray-100 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-700 hover:-translate-y-1 transition-all duration-300 cursor-default"
                style={{
                  boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
                  opacity: benefitsVisible ? 1 : 0,
                  transform: benefitsVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.96)',
                  transition: `opacity 0.5s ease ${index * 80 + 200}ms, transform 0.5s cubic-bezier(0.34,1.4,0.64,1) ${index * 80 + 200}ms`,
                }}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${benefit.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" ref={reqsRef} className="w-full py-20 sm:py-28 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #fdf2f8 50%, #fff7ed 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-14 sm:mb-18"
            style={{
              opacity: reqsVisible ? 1 : 0,
              transform: reqsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-purple-200 mb-5">
              <i className="ri-list-check-2 text-purple-600"></i>
              <span className="text-xs font-bold uppercase tracking-widest text-purple-700">Qualification</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-4">
              What You Need to <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Qualify</span>
            </h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
              We keep the bar reasonable. Show us consistency, authenticity, and a real community, and you're in.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {requirements.map((req, index) => (
              <div
                key={req.label}
                className="group bg-white rounded-2xl p-6 sm:p-7 border border-gray-100 hover:border-purple-200 hover:-translate-y-1 transition-all duration-300 text-center cursor-default"
                style={{
                  boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
                  opacity: reqsVisible ? 1 : 0,
                  transform: reqsVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.96)',
                  transition: `opacity 0.5s ease ${index * 100 + 200}ms, transform 0.5s cubic-bezier(0.34,1.4,0.64,1) ${index * 100 + 200}ms`,
                }}
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${req.icon} text-2xl text-white`}></i>
                </div>
                <div className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                  {req.number}
                </div>
                <div className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">{req.label}</div>
                <p className="text-xs text-gray-500 leading-relaxed">{req.description}</p>
              </div>
            ))}
          </div>

          {/* Note */}
          <div
            className="mt-10 sm:mt-12 max-w-2xl mx-auto text-center bg-white rounded-2xl p-6 border border-purple-100"
            style={{
              opacity: reqsVisible ? 1 : 0,
              transition: 'opacity 0.6s ease 0.6s',
            }}
          >
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="text-gray-900">Don't meet all requirements yet?</strong> Keep creating and growing. We regularly review active creators and reach out to those who show strong potential. You can also <a href="/auth/creator-register" className="text-purple-600 font-semibold hover:underline">apply anytime</a> and we'll review your profile.
            </p>
          </div>
        </div>
      </section>

      {/* Revenue Tiers Section */}
      <section id="revenue" ref={tiersRef} className="w-full bg-white dark:bg-gray-950 py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div
            className="text-center mb-14 sm:mb-18"
            style={{
              opacity: tiersVisible ? 1 : 0,
              transform: tiersVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 mb-5">
              <i className="ri-money-dollar-circle-line text-green-600 dark:text-green-400"></i>
              <span className="text-xs font-bold uppercase tracking-widest text-green-700 dark:text-green-300">Revenue Tiers</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white leading-tight mb-4">
              The More You Grow, <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">The More You Keep</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-relaxed">
              Simple, transparent revenue tiers. No hidden fees, no complicated math. Just you and your earnings.
            </p>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center justify-center gap-2 mb-10 sm:mb-12">
            {revenueTiers.map((t, i) => (
              <button
                key={t.tier}
                onClick={() => setActiveTab(i)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === i
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {t.tier}
              </button>
            ))}
          </div>

          {/* Active tier card */}
          <div
            className="relative rounded-3xl p-px overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #a855f7, #ec4899, #f97316)',
              opacity: tiersVisible ? 1 : 0,
              transform: tiersVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.96)',
              transition: 'opacity 0.5s ease 0.2s, transform 0.5s cubic-bezier(0.34,1.4,0.64,1) 0.2s',
            }}
          >
            <div className="bg-white dark:bg-gray-900 rounded-[22px] p-8 sm:p-12 text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${revenueTiers[activeTab].color} mb-6`}>
                <i className="ri-vip-crown-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-2">
                {revenueTiers[activeTab].tier}
              </h3>
              <div className="text-6xl sm:text-7xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {revenueTiers[activeTab].share}
              </div>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                Revenue share, you keep this much of every dollar
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-full">
                  <i className="ri-user-line text-purple-500"></i>
                  Min. {revenueTiers[activeTab].followers} followers
                </span>
                <span className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-full">
                  <i className="ri-shield-check-line text-green-500"></i>
                  No hidden fees
                </span>
                <span className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-full">
                  <i className="ri-time-line text-orange-500"></i>
                  Weekly payouts
                </span>
              </div>
            </div>
          </div>

          {/* Comparison mini-table */}
          <div
            className="mt-8 grid grid-cols-3 gap-3"
            style={{
              opacity: tiersVisible ? 1 : 0,
              transition: 'opacity 0.6s ease 0.4s',
            }}
          >
            {revenueTiers.map((t) => (
              <div
                key={t.tier}
                className={`rounded-xl p-4 text-center border-2 transition-all cursor-pointer ${
                  activeTab === revenueTiers.indexOf(t)
                    ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700'
                }`}
                onClick={() => setActiveTab(revenueTiers.indexOf(t))}
              >
                <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t.tier}</div>
                <div className="text-2xl font-black text-gray-900 dark:text-white">{t.share}</div>
                <div className="text-xs text-gray-400 dark:text-gray-500">{t.followers} followers</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Apply Section */}
      <section className="w-full py-20 sm:py-28 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 40%, #7c3aed 80%, #db2777 100%)' }}>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <i className="ri-send-plane-line text-white"></i>
            <span className="text-xs font-bold uppercase tracking-widest text-white">Application</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
            Ready to Level Up?<br />
            <span className="bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">Apply in Minutes</span>
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
            No lengthy forms. No waiting weeks. Tell us about yourself to Vibe-z Profile in App, link your content, and we'll review your application within 48 hours.
          </p>

          <div className="grid sm:grid-cols-2 gap-5 mb-12 max-w-2xl mx-auto">
            {[
              { step: '1', title: 'Create Account', desc: 'Sign up on Vibe-Z and complete your creator profile.', icon: 'ri-user-add-line' },
              { step: '2', title: 'Get Verified', desc: 'Our team reviews and responds within 48 hours.', icon: 'ri-verified-badge-line' },
            ].map((s) => (
              <div key={s.step} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-left">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <i className={`${s.icon} text-white text-lg`}></i>
                  </div>
                  <span className="text-xs font-bold text-white/60 uppercase tracking-wider">Step {s.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{s.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <a
            href="/auth/creator-register"
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-black text-gray-900 bg-white rounded-full hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-rocket-line text-xl"></i>
            Start Your Application
          </a>
          <p className="text-sm text-white/50 mt-4">Free to apply · No commitment · 48h response time</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <Link to="/" className="inline-block mb-4">
                <img src="https://public.readdy.ai/ai/img_res/28f622dd-215d-431d-8f03-9f76adf8a1b8.png" alt="Vibe-Z Logo" className="h-14 w-auto" />
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Empowering creators to build sustainable income and meaningful connections with their audience.
              </p>
              <div className="flex gap-3">
                <a href="https://x.com/Vibez_Social" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 flex items-center justify-center transition-all">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/vibez_social/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 flex items-center justify-center transition-all">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Partner Program</h4>
              <ul className="space-y-3">
                <li><a href="#benefits" className="text-white hover:text-purple-400 transition-colors">Benefits</a></li>
                <li><a href="#requirements" className="text-white hover:text-purple-400 transition-colors">Requirements</a></li>
                <li><a href="#revenue" className="text-white hover:text-purple-400 transition-colors">Revenue Tiers</a></li>
                <li><a href="/auth/creator-register" className="text-white hover:text-purple-400 transition-colors">Apply Now</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><a href="/page/terms-of-service" className="text-white hover:text-purple-400 transition-colors">Terms</a></li>
                <li><a href="/page/privacy-policy" className="text-white hover:text-purple-400 transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">&copy; 2026 Vibe-Z. All rights reserved.</p>
            <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">Back to Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}